/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Delete from "@eisinc/XDL/Icons/Delete";
import color from "@eisinc/XDL/Utilities/color";
import { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css, keyframes } from "styled-components";

type BarcodeScannerStyles = {
    barcodeIcon: IconProps;
    barcodeButton: ButtonPresentationProps;
    mobileBarcodeModal: ModalPresentationProps;
    modalFooter: InjectableCss;
    notFoundProductMsg: TypographyProps;
    learnMore: LinkPresentationProps;
    resultSection: InjectableCss;
    placeholderMessage: TypographyProps;
    grayPlaceholder: InjectableCss;
    sucessAtcMsg: TypographyProps;
    productDetails: InjectableCss;
    productTitleText: TypographyProps;
    labelText: TypographyProps;
    valueText: TypographyProps;
    brandLink: LinkPresentationProps;
    productImageWrapper: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    removeCartLineText: TypographyProps;
    removeCartLineBtn: InjectableCss;
    cartActionsContainer: InjectableCss;
    rightSide: InjectableCss;
    leftSide?: InjectableCss;
    qty?: TextFieldProps;
    productPrice: InjectableCss;
    uom: InjectableCss;
    unitOfMeasureSelect?: SelectPresentationProps;
    unitOfMeasureSingleUOM?: SelectPresentationProps;
    updateButton?: ButtonPresentationProps;
    addingToCartWrapper: InjectableCss;
};

const snapshotEffectKeyframes = keyframes`
    0% {
        max-height: 0;
        max-width: 0;
        top: 50%;
        left: 50%;
    }

    50% {
        max-height: 80%;
        max-width: 80%;
        top: 50%;
        left: 50%;
    }

    100% {
        max-width: 0;
        max-height: 0;
        top: 100%;
        left: 0;
    }
`;

const barcodeScannerStyles: BarcodeScannerStyles = {
    barcodeIcon: {
        size: 26,
        color: "common.black",
    },
    barcodeButton: {
        css: css`
            position: absolute;
            right: 40px;
            z-index: 1;
            top: 50%;
            transform: translateY(-50%);
            padding: 0 12px 0 15px;
            background-color: ${color("common.background")};
        `,
    },
    mobileBarcodeModal: {
        closeButtonProps: {
            css: css`
                background-color: transparent;
                border: none;
                height: 28px;
                padding: 0;
                &:hover {
                    background-color: transparent;
                    border-color: transparent;
                }
                ${IconWrapper} {
                    color: ${color("common.inverse")};
                }
            `,
        },
        cssOverrides: {
            modalContainer: css`
                min-height: 0;
                height: 100vh;
                height: -webkit-fill-available;
                overflow: auto;
                align-items: flex-start;
                z-index: 100;
            `,
            modalTitle: css`
                top: 0;
                left: 0;
                width: 100%;
                background-color: ${color("common.black", 0.66)};
                padding: 10px;
                height: 44px;
                z-index: 100;

                > h4 {
                    font-size: 20px;
                    margin: 0;
                    padding: 0;
                    font-weight: 500;
                    letter-spacing: 0;
                    line-height: 24px;
                    color: ${color("common.inverse")};
                }
            `,
            modalContent: css`
                width: auto;
                height: auto;
                overflow-y: visible;
                max-height: none;
                > .modal-actions {
                    padding: 25px 20px;
                    background-color: ${getColor("common.ui3")};
                    display: flex;
                    justify-content: flex-end;

                    button {
                        margin-right: 20px;

                        &:last-child {
                            margin-right: 0;
                        }
                    }
                }
            `,
            modalBody: css`
                width: 100%;
                height: auto !important;
                max-height: none !important;

                .viewoport-container {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    max-height: 100%;

                    .detection {
                        position: absolute;
                        bottom: 0;
                        width: 100%;
                        height: 40px;
                        background-color: ${color("common.backgroundContrast", 0.5)};
                        z-index: 100;
                        display: flex;
                        justify-content: space-between;
                        padding: 0 10px;
                        align-items: center;

                        img {
                            position: relative;
                            bottom: 5px;
                            max-width: 100px;
                            max-height: 40px;
                            width: auto;
                            height: auto;
                            border: 2px solid ${getColor("common.white")};
                            box-shadow: 4px 4px 4px ${color("common.backgroundContrast", 0.25)};
                        }

                        .barcode-number {
                            display: inline-block;
                            margin: 0 10px;
                            line-height: 40px;
                            font-size: 18px;
                            letter-spacing: 1px;
                            font-weight: 500;
                            color: ${getColor("common.white")};
                        }
                    }
                }

                .viewport {
                    position: relative;
                    float: left;
                    display: inline-block;

                    video {
                        display: block;
                        width: 100%;
                        max-width: 100%;
                        max-height: 100%;
                        margin: auto;
                    }

                    .drawingBuffer {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        left: 0;
                        top: 0;
                    }

                    [clear="all"] {
                        display: none;
                    }

                    .screenshot-of-bar-code {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        border: 3px solid ${getColor("common.white")};
                        z-index: 2;
                        visibility: hidden;
                        width: 0;
                        height: 0;
                    }

                    .screenshot-of-bar-code.img-snapshot-effect {
                        animation-name: ${snapshotEffectKeyframes};
                        animation-duration: 0.6s;
                        animation-fill-mode: forwards;
                        width: auto;
                        height: auto;
                        visibility: visible;
                    }
                }
            `,
        },
    },
    modalFooter: {
        css: css`
            width: 100%;
            background: ${color("common.background")};
            padding: 11px 10px 18px 10px;
            line-height: 18px;
        `,
    },
    notFoundProductMsg: {
        color: "danger",
        size: 14,
        lineHeight: "18px",
        css: css`
            margin-bottom: 10px;
        `,
    },
    learnMore: {
        typographyProps: {
            size: 14,
            lineHeight: "18px",
        },
        css: css`
            margin-top: 10px;
        `,
    },
    resultSection: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    placeholderMessage: {
        size: 14,
        lineHeight: "18px",
        color: "text.main",
    },
    grayPlaceholder: {
        css: css`
            min-height: 270px;
            background-color: ${color("common.gray")};
        `,
    },
    sucessAtcMsg: {
        color: "common.white",
        size: 14,
        lineHeight: "18px",
        css: css`
            display: block;
            margin-bottom: 0;
            background: ${color("success")};
            padding: 10px;
            text-align: center;
            &.add-margin {
                margin-bottom: 16px;
            }
        `,
    },
    productDetails: {
        css: css``,
    },
    productTitleText: {
        size: 14,
        lineHeight: "20px",
        color: "text.main",
        css: css`
            margin-bottom: 10px;
            display: block;
        `,
    },
    labelText: {
        size: 14,
        lineHeight: "18px",
        weight: "bold",
        css: css`
            margin-right: 2px;
        `,
    },
    valueText: {
        size: 14,
        lineHeight: "12px",
        css: css`
            margin-bottom: 4px;
        `,
    },
    brandLink: {
        typographyProps: {
            size: 14,
            lineHeight: "18px",
        },
        css: css`
            margin-bottom: 10px;
        `,
    },
    productImageWrapper: {
        css: css`
            height: 270px;
            display: flex;
            justify-content: center;
            align-items: center;

            img {
                max-height: 100%;
            }
        `,
    },
    removeCartLineIcon: { src: Delete, color: "primary.main", size: 16 },
    removeCartLineText: {
        size: 12,
        lineHeight: "13px",
        css: css`
            padding: 0 4px;
        `,
    },
    removeCartLineBtn: {
        css: css`
            margin: 0 auto;
        `,
    },
    cartActionsContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 15px;
        `,
    },
    rightSide: {
        css: css`
            display: flex;
            align-items: end;
            justify-content: end;
        `,
    },

    productPrice: {
        css: css`
            width: 100%;
            font-size: 24px;
            height: 24px;
            margin-top: 16px;
        `,
    },
    uom: {
        css: css`
            padding-left: 12px;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formInputWrapper: css`
                &&& select + span {
                    top: 0;
                }
            `,
            formField: css`
                select {
                    height: 32px;
                    width: 96px !important;
                }
            `,
        },
        labelProps: {
            css: css`
                display: none;
            `,
        },
    },
    unitOfMeasureSingleUOM: {
        cssOverrides: {
            formInputWrapper: css`
                &&& select + span {
                    top: 25%;
                    width: 14px;
                    height: 14px;
                    padding: 0;
                }
            `,
            formField: css`
                select {
                    height: 32px;
                    width: 96px !important;
                }
            `,
        },
        labelProps: {
            css: css`
                display: none;
            `,
        },
    },
    updateButton: {
        css: css`
            width: 100%;
            margin: 20px 0;
            font-size: 14px;
            line-height: 16px;
        `,
    },
    addingToCartWrapper: {
        css: css`
            text-align: center;
        `,
    },
};

export default barcodeScannerStyles;
